import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Segment } from 'semantic-ui-react';

import { maskValue } from '../../../services/masks';

import { deleteAgencyLocation, getAgencyLocation, getAgencyLocationList } from '../../../api/nora/agencies';

import { useAgencyAction, useAgencyState } from '../../../contexts/AgenciesContext';
import { useAuthState } from '../../../contexts/AuthContext';
import { useUIAction } from '../../../contexts/UIContext';

import Address from '../../Table/Cells/Address';
import Alert from '../../Alert';
import HelpModal from '../../Shared/HelpModal';
import LocationsMobileColumn from './LocationsMobileColumn';
import LocationsModal from '../../ManagementPortal/Agencies/Directory/AgencyDetails/LocationsTab/LocationsModal';
import multiWordFilterFn from '../../Table/filterFunctions/multiWordFilterFn';
import Table from '../../Table';
import TableRowActions from '../../Table/Cells/TableRowActions';

import styles from '../agency-portal.module.css';

const Locations = () => {
	const { agency } = useAgencyState();
	const { setAgencyLocations } = useAgencyAction();
	const { user: { permissions } } = useAuthState();
	const { setApiErrorAlert, setInfobarLoader } = useUIAction();

	const [ location, setLocation ] = useState( null );
	const [ deleteConfirm, setDeleteConfirm ] = useState( false );
	const [ locationToDelete, setLocationToDelete ] = useState( {} );
	const [ width, setWidth ] = useState( window.innerWidth );

	useEffect( () => {
		const handleResize = () => setWidth( window.innerWidth );

		window.addEventListener( 'resize', handleResize );

		return () => window.removeEventListener( 'resize', handleResize );
	}, [] );

	const retrieveLocationList = useCallback( async () => {
		setInfobarLoader( 'Retrieving Location List...' );

		try {
			const data = await getAgencyLocationList( agency?.id );

			setAgencyLocations( data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setInfobarLoader,
		agency,
		setAgencyLocations,
		setApiErrorAlert
	] );

	const addLocation = useCallback( () => {
		setLocation( {} );
	}, [] );

	// const locationActions = useMemo( () => [
	// 	{
	// 		action: addLocation,
	// 		icon: 'add',
	// 		permission: true,
	// 		text: 'Add Location'
	// 	}
	// ], [ addLocation ] );

	const closeLocationModal = useCallback( () => {
		setLocation( null );
	}, [] );

	const handleDeleteLocation = useCallback( async () => {
		setInfobarLoader( 'Retrieving Location List...' );

		try {
			await deleteAgencyLocation( agency?.id, locationToDelete.id );
			await retrieveLocationList();
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setInfobarLoader,
		agency,
		locationToDelete.id,
		retrieveLocationList,
		setApiErrorAlert
	] );

	const deleteConfirmMessage = useMemo( () => <span>Are you sure you would like to remove <strong>{ `${ locationToDelete.name }` }</strong> from the list?</span>, [ locationToDelete ] );

	const deleteConfirmCloseModal = useCallback( () => setDeleteConfirm( false ), [] );

	// Data Table
	const addressCell = useCallback( ( { row } ) => <Address row={ row.original } />, [] );

	const locationsMobileColumn = useCallback( ( { row } ) => <LocationsMobileColumn row={row.original} />, [] );

	const onEdit = useCallback( async ( { id } ) => {
		setInfobarLoader( 'Retrieving Location Info...' );

		try {
			const location = await getAgencyLocation( agency.id, id );

			setLocation( location );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		agency?.id,
		setApiErrorAlert,
		setInfobarLoader
	] );

	const onDelete = useCallback( location => {
		setLocationToDelete( location );
		setDeleteConfirm( true );
	}, [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onEdit,
				componentProps: { color: 'pink' },
				icon: 'search',
				permission: permissions.agencyEditAgency.canExecute,
				text: 'Edit Location'
			},
			{
				action: onDelete,
				componentProps: { color: 'red' },
				icon: 'trash',
				permission: permissions.agencyEditAgency.canExecute,
				text: 'Delete Location'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [ onDelete, onEdit, permissions.agencyEditAgency.canExecute ] );

	const columns = useMemo( () => [
		{
			accessor: 'name',
			Header: 'Name',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			id: 'address',
			accessor: row => `${ row.address }${ row.address2 }${ row.city }${ row.state }${ row.zip }`,
			Header: 'Address',
			filter: multiWordFilterFn,
			Cell: addressCell,
			minWidth: 60
		},
		{
			accessor: row => `${ maskValue( 'phone', row.phone.number ) }`,
			Header: 'Phone',
			filter: multiWordFilterFn,
			minWidth: 60
		}
	], [ addressCell ] );

	const mobileColumns = useMemo( () => [
		{
			id: 'mobile_column',
			accessor: row => `${ row.name }${ row.address }${ row.address2 }${ row.city }${ row.state }${ row.zip }${ maskValue( 'phone', row.phone.number ) }`,
			Header: 'Locations',
			filter: multiWordFilterFn,
			Cell: locationsMobileColumn,
			minWidth: 60
		}

	], [ locationsMobileColumn ] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Cell: tableRowActionsCell,
		Header: 'View / Delete',
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.agencyEditAgency.isAvailable
			? [
				...width < 767 ? mobileColumns : columns,
				actionColumn
			]
			: width < 767 ? mobileColumns : columns,
		[
			actionColumn,
			columns,
			mobileColumns,
			permissions.agencyEditAgency.isAvailable,
			width
		]
	);

	return (
		<div className={ styles.container } >
			<header className={ styles.pageHeader }>
				<h2>Locations
					<HelpModal
						id={ 8 }
						triggerStyles={ { verticalAlign: 'top' } }
					/>
				</h2>
				<Button
					disabled={ !permissions.agencyEditAgency.canExecute }
					icon="plus"
					inverted
					onClick={ addLocation }
					size="tiny"
				/>
			</header>
			<Segment
				basic
				as="div"
				className={ styles.content }
			>
				<Table
					columns={ renderColumns }
					data={ agency.locations }
					showCount={ agency.locations.length > 10 }
					showEmptyRows={ false }
					showPagination={ agency.locations.length > 10 }
					style={ { overflowX: 'auto' } }
				/>
			</Segment>
			<LocationsModal
				closeModal={ closeLocationModal }
				location={ location }
				onSave={ retrieveLocationList }
				open={ Boolean( location ) }
			/>
			<Alert
				action={ handleDeleteLocation }
				name="unassign-coordination-confirm-alert"
				message={ deleteConfirmMessage }
				open={ deleteConfirm }
				closeModal={ deleteConfirmCloseModal }
				type="delete"
			/>
		</div>
	);
};

export default Locations;
